import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  ActivityCategoryDTO,
  ActivityDTO,
  ActivitySpeciesDTO,
  ActivitySpeciesTypeDTO,
  ActivityStatusDTO,
  ActivityTypeDTO,
  AuditLogDTO,
  CountryDTO,
  GenderDTO,
  MemberDTO,
  MemberFirearmDTO,
  ProvinceDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useActivityTypes } from '../../hooks/useActivityTypeApi';
import { ActivityTypeConstants } from '../../constants/ActivityTypeConstants';
import { useActivityCategories, useActivityCategorySearch } from '../../hooks/useActivityCategoryApi';
import {
  ActivityCategoryConstants,
  AnimalHunting,
  ExcludeMemberFirearm,
  IncludeDescriptionOfActivity,
  IncludeNatureOfMeeting,
  IncludeTypeActivity,
} from '../../constants/ActivityCategoryConstants';
import { useCountries } from '../../hooks/useCountryApi';

import { useMemberFirearmApi } from '../../hooks/useMemberFirearmApi';
import { getMemberId, getRoles } from '../../utils/tokenUtils';
import ImageLink from '../common/ImageLink';
import FileUpload from '../common/FileUpload';
import { useFileApi } from '../../hooks/useFileApi';
import { useMemberApi } from '../../hooks/useMemberApi';
import { useActivityApi } from '../../hooks/useActivityApi';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { useActivityStatuses } from '../../hooks/useActivityStatusApi';
import AuditLogTable from '../tables/AuditLogTable';
import { useAuditLogApi } from '../../hooks/useAuditLogApi';
import { renderBooleanBadge } from '../../utils/displayUtils';
import Drawer from '../common/Drawer';
import Button from '../common/Button';
import ActivitySpeciesTable from '../tables/ActivitySpeciesTable';
import { confirmDelete, confirmAction } from '../../utils/sweetAlertUtils';
import AddButton from '../common/AddButton';
import { useActivitySpeciesTypeApi } from '../../hooks/useActivitySpeciesTypeApi';
import { useGenders } from '../../hooks/useGenderApi';
import { provinceLogic } from '../../utils/provinceLogic';
import { CountryConstant } from '../../constants/CountryConstants';
import { ProvinceConstant } from '../../constants/ProvinceConstants';
import FeedbackForm from './FeedbackForm';
import { ComponentConstants } from '../../constants/ComponentConstants';
import MemberDetails from '../common/MemberDetails';
import { FileTypeConstants } from '../../constants/FileTypeConstants';
import { buildFirearmOptions } from '../../utils/formUtils/buildFirearmOptions';
import { ActivitySpeciesTypeConstants } from '../../constants/ActivitySpeciesTypeConstants';

interface ActivitySpeciesItem extends ActivitySpeciesDTO {
  arrayIndex?: number;
}

interface ActivityFormProps {
  initialValues: ActivityDTO;
  onSubmit: (data: ActivityDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const defaultSpeciesItem: ActivitySpeciesItem = {};

const ActivityForm: React.FC<ActivityFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<ActivityDTO>(initialValues);
  const [speciesItem, setSpeciesItem] =
    useState<ActivitySpeciesItem>(defaultSpeciesItem);
  const memberId = getMemberId();
  const [member, setMember] = useState<MemberDTO>();
  const [formResetKey, setFormResetKey] = useState(0);
  const { getFeedback } = useActivityApi();

  // Feedback HTML is stored in blob storage now, not returned inline with the Activity DTO — fetch it on demand.
  useEffect(() => {
    if (!formData.id) return;
    getFeedback(formData.id)
      .then((feedback) => setFormData((prev) => ({ ...prev, feedback })))
      .catch(() => { });
  }, [formData.id]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  const statusFieldName = 'activityStatusId'

  const roles = getRoles();
  const FORM_SECTIONS = {
    ...(roles?.includes('Admin')
      ? {
        memberDetails: {
          id: 'member-details',
          title: 'Member Details',
        },
      }
      : {}),
    ...(roles?.includes('User')
      ? {
        activityDetails: {
          id: 'activity-details',
          title: 'Activity Details',
        },
      }
      : {
        activityDetails: {
          id: 'record-details',
          title: 'Record Details',
        },
      }),

    specifySpecies: {
      id: 'specify-species',
      title: 'Specify species hunted',
    },
    ...(roles?.includes('User')
      ? {
        uploadProof: {
          id: 'upload-proof',
          title: 'Upload proof of activity',
        },
      }
      : {
        uploadProof: {
          id: 'confirmation-uploads',
          title: 'Confirmation Uploads',
        },
      }),

    ...(roles?.includes('Admin')
      ? {
        feedback: {
          id: 'feedback',
          title: 'Feedback',
        },
        notes: {
          id: 'notes',
          title: 'Notes',
        },
        history: {
          id: 'history',
          title: 'History',
        },
      }
      : {}),
  } as const;

  const [isSpeciesItemDrawerOpen, setIsSpeciesItemDrawerOpen] = useState(false);

  const [activityCategories, setActivityCategories] = useState<ActivityCategoryDTO[]>([]);

  const [memberFirearms, setMemberFirearms] = useState<MemberFirearmDTO[]>([]);
  const [activitySpeciesTypes, setActivitySpeciesTypes] = useState<ActivitySpeciesTypeDTO[]>([]);

  const [years, setYears] = useState<string[]>([]);

  const { uploadFile, getFileUrl } = useFileApi();
  const { getById: getMemberById } = useMemberApi();
  const { data: activityStatusesData = [], isLoading: isLoadingActivityStatuses } = useActivityStatuses();
  const { data: activityTypesData = [], isLoading: isLoadingActivityTypes } = useActivityTypes();
  const { data: activityCategoriesData = [], isLoading: isLoadingActivityCategories } = useActivityCategories();
  const { search: searchActivityCategories } = useActivityCategorySearch();
  const { data: countriesData, isLoading: isLoadingCountries } = useCountries();
  const { search: searchMemberFirearms, isLoading: isLoadingMemberFirearms } = useMemberFirearmApi();
  const { search: searchActivitySpeciesTypes, isLoading: isLoadingActivitySpeciesTypes, } = useActivitySpeciesTypeApi();
  const { data: gendersData, isLoading: isLoadingGenders } = useGenders();
  const { search: searchAuditLogs, isLoading: isLoadingAuditLogs } = useAuditLogApi();

  const [auditLogs, setAuditLogs] = useState<AuditLogDTO[]>([]);
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);

  const { provinceOptions, isLoadingProvinces, shouldShowProvinceOther } = provinceLogic({
    countryId: formData.countryId,
    provinceId: formData.provinceId,
    provinceOther: formData.provinceOther,
    isExistingMember: !!initialValues.id,
    autoSetNotOnList: true,
    onProvinceChange: (newProvinceId, newProvinceOther) => {
      setFormData((prev) => ({
        ...prev,
        provinceId: newProvinceId,
        provinceOther: newProvinceOther,
      }));
    },
  });

  const validationRules: ValidationRules = {
    activityTypeId: [{ required: true, message: 'Activity Type is required' }],
    nonActiveTypeId: [
      {
        required:
          formData.activityTypeId === ActivityTypeConstants.NON_ACTIVE_REPORT
            ? true
            : false,
        message: 'Non-Activity Type is required',
      },
    ],
    dateYear: [
      {
        required:
          formData.activityTypeId === ActivityTypeConstants.NON_ACTIVE_REPORT
            ? true
            : false,
        message: 'Date Year is required',
      },
    ],
    activityReason: [
      {
        required:
          formData.activityTypeId === ActivityTypeConstants.NON_ACTIVE_REPORT ||
            IncludeNatureOfMeeting.includes(formData.activityCategoryId ?? '') ||
            IncludeDescriptionOfActivity.includes(
              formData.activityCategoryId ?? '',
            )
            ? true
            : false,
        message: 'Activity Reason is required',
      },
    ],
    activityCategoryId: [
      {
        required:
          formData.activityTypeId === ActivityTypeConstants.HUNT_RELATED ||
            formData.activityTypeId === ActivityTypeConstants.SPORT_SHOOTING
            ? true
            : false,
        message: 'Type of activity is required',
      },
    ],
    leaveDate: [
      {
        required: !formData.isQuickEvent &&
          AnimalHunting.includes(formData.activityCategoryId ?? '')
          ? true
          : false,
        message: 'Activity leave date is required',
      },
    ],
    returnDate: [
      {
        required: !formData.isQuickEvent &&
          AnimalHunting.includes(formData.activityCategoryId ?? '')
          ? true
          : false,
        message: 'Activity return date is required',
      },
    ],
    countryId: [
      {
        required:
          !formData.isQuickEvent &&
            (formData.activityTypeId === ActivityTypeConstants.HUNT_RELATED ||
              formData.activityTypeId === ActivityTypeConstants.SPORT_SHOOTING)
            ? true
            : false,
        message: 'Country is required',
      },
    ],
    provinceId: [
      {
        required:
          (formData.activityTypeId === ActivityTypeConstants.HUNT_RELATED ||
            formData.activityTypeId === ActivityTypeConstants.SPORT_SHOOTING) &&
          (formData.countryId === CountryConstant.SOUTH_AFRICA ||
            !formData.countryId),
        message: 'Province is required',
      },
    ],
    provinceOther: [
      {
        required:
          (formData.activityTypeId === ActivityTypeConstants.HUNT_RELATED ||
            formData.activityTypeId === ActivityTypeConstants.SPORT_SHOOTING) &&
          formData.countryId !== CountryConstant.SOUTH_AFRICA &&
          !!formData.countryId,
        message: 'Province Other is required',
      },
    ],
    town: [
      {
        required:
          formData.activityTypeId === ActivityTypeConstants.HUNT_RELATED ||
            formData.activityTypeId === ActivityTypeConstants.SPORT_SHOOTING
            ? true
            : false,
        message: 'Town is required',
      },
    ],
    location: [
      {
        required:
          formData.activityTypeId === ActivityTypeConstants.HUNT_RELATED ||
            formData.activityTypeId === ActivityTypeConstants.SPORT_SHOOTING
            ? true
            : false,
        message: 'Location is required',
      },
    ],
    memberFirearmId: [
      {
        required: !formData.isQuickEvent &&
          formData.activityCategoryId !== undefined &&
          !ExcludeMemberFirearm.includes(formData.activityCategoryId),
        message: 'Firearm is required',
      },
    ],
    activityDate: [
      {
        required:
          !!formData.activityCategoryId &&
          !AnimalHunting.includes(formData.activityCategoryId),
        message: 'Activity Date is required',
      },
      {
        custom: (value) => {
          const isRequired =
            !!formData.activityCategoryId &&
            !AnimalHunting.includes(formData.activityCategoryId) &&
            !formData.isQuickEvent;

          if (!isRequired && (!value || value.trim() === '')) {
            return true;
          }

          return !isNaN(Date.parse(value));
        },
        message: 'Activity Date must be a valid date',
      },
    ],
    typeActivity: [
      {
        required: IncludeTypeActivity.includes(
          formData.activityCategoryId ?? '',
        )
          ? true
          : false,
        message: 'Type Activity is required',
      },
    ],

    confirmationFileId: [
      { required: !formData.isQuickEvent, message: 'Activity file is required' },
    ],
  };

  const speciesItemValidationRules: ValidationRules = {
    activitySpeciesTypeId: [{ required: true, message: 'Type is required' }],
    genderId: [{ required: true, message: 'Gender is required' }],
    total: [
      { required: true, message: 'Total is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Amount must be a positive number',
      },
    ],
  };

  const { errors, validate } = useFormValidation<ActivityDTO>(validationRules);
  const { errors: speciesItemErrors, validate: speciesItemValidate } = useFormValidation<ActivitySpeciesDTO>(speciesItemValidationRules);

  const fetchMember = async () => {
    try {
      const response = await getMemberById(memberId ?? '');
      setMember(response?.data);
    } catch (error) {
      console.error('Error fetching Member', error);
    }
  };

  const fetchActivityCategories = async () => {
    try {
      const response = await searchActivityCategories({
        filters: {
          activityTypeId: formData.activityTypeId,
          isActive: true,
        },
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setActivityCategories(response?.results || []);
    } catch (error) {
      console.error('Error fetching Activity Categories', error);
    }
  };



  const fetchMemberFirearms = async () => {
    try {
      const response = await searchMemberFirearms({
        filters: {
          memberId: memberId || formData.memberId,
        },
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setMemberFirearms(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching Member Firearms', error);
    }
  };

  const fetchActivitySpeciesTypes = async () => {
    try {
      const response = await searchActivitySpeciesTypes({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setActivitySpeciesTypes(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching Activity Species Types', error);
    }
  };


  useEffect(() => {
    fetchMember();
    fetchActivitySpeciesTypes();
    generateYears();
    if (initialValues.activityTypeId) {
      fetchActivityCategories();
    }
  }, []);

  useEffect(() => {
    fetchMemberFirearms();
  }, [memberId]);

  useEffect(() => {
    if (formData.activityTypeId) {
      fetchActivityCategories();
      setFormData((prev) => ({
        ...initialValues,
        activityTypeId: prev.activityTypeId,
      }));
      setFormResetKey((prev) => prev + 1);
    }
  }, [formData.activityTypeId]);

  useEffect(() => {
    if (formData.activityTypeId) {
      setFormData((prev) => ({
        ...initialValues,
        activityTypeId: prev.activityTypeId,
        activityCategoryId: prev.activityCategoryId,
      }));
    }
  }, [formData.activityCategoryId]);

  useEffect(() => {
    if (formData.leaveDate) {
      setFormData((prev) => ({
        ...prev,
        activityDate: formData.leaveDate,
      }));
    }
  }, [formData.leaveDate]);

  useEffect(() => {
    if (formData.activityTypeId === ActivityTypeConstants.NON_ACTIVE_REPORT) {
      setFormData((prev) => ({
        ...prev,
        countryId: CountryConstant.SOUTH_AFRICA,
      }));
    }
  }, [formData.activityTypeId]);

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const yearsList = [];

    for (let i = 0; i < 3; i++) {
      yearsList.push((currentYear - i).toString());
    }

    setYears(yearsList);
  };

  const handleSave = async () => {
    // Validate first before showing confirmation
    if (!validate(formData)) {
      return;
    }

    // For regular users, show confirmation dialog after validation (except for non-active reports)
    if (roles?.includes('User') && formData.activityTypeId !== ActivityTypeConstants.NON_ACTIVE_REPORT) {
      const confirmed = await confirmAction({
        title: 'Confirm Activity Details',
        text: `I, ${member?.firstName} ${member?.lastName} (${member?.idPassportNumber}), hereby confirm that the hunt/sport shooting related activity entered is correct and was done by myself on the date: ${formData.activityDate ?? ''} at location: ${formData.location ?? ''}.`,
        icon: 'warning',
        confirmButtonText: 'Yes, I confirm',
        cancelButtonText: 'Cancel',
      });

      if (!confirmed) return;
    }

    // Proceed with submission
    const submissionData = { ...formData };

    if (submissionData.provinceId === ProvinceConstant.NOT_ON_LIST) {
      submissionData.provinceId = undefined;
    }

    onSubmit(submissionData);
  };

  useEffect(() => {
    const fetchAuditLogs = async () => {
      if (initialValues.id) {
        try {
          const response = await searchAuditLogs({
            page: auditCurrentPage,
            pageSize: auditPageSize,
            sortBy: 'CreatedUtc',
            sortDescending: true,
            filters: {
              tableName: 'Activity',
              recordId: initialValues.id,
            },
          });

          if (response && response?.success && response?.data) {
            setAuditLogs(response.data.results as AuditLogDTO[]);
            setAuditTotalPages(
              Math.ceil((response.data.totalCount ?? 0) / auditPageSize),
            );
          }
        } catch (error) {
          console.error('Error fetching audit logs:', error);
        }
      }
    };

    fetchAuditLogs();
  }, [initialValues.id, auditCurrentPage, auditPageSize]);

  const handleFileChange =
    (field: keyof ActivityDTO) =>
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          try {
            setUploadingFiles((prev) => ({ ...prev, [field]: true }));
            const response = await uploadFile(
              'files',
              file.name,
              file,
              'activities',
            );
            if (response && response.success && response.data) {
              setFormData((prev) => ({
                ...prev,
                [field]: response.data,
              }));
            }
          } catch (error) {
            console.error('Error handling file:', error);
          } finally {
            setUploadingFiles((prev) => ({ ...prev, [field]: false }));
          }
        }
      };

  const handleDrawerClose = () => {
    setSpeciesItem(defaultSpeciesItem);
    setIsSpeciesItemDrawerOpen(false);
  };

  const handleSpeciesItemSave = () => {
    if (speciesItem && speciesItemValidate(speciesItem)) {
      setFormData((prev) => {
        const existingSpeciesItems =
          (prev.activitySpecies as ActivitySpeciesItem[]) || [];

        let existingIndex = -1;

        if (speciesItem.id) {
          existingIndex = existingSpeciesItems.findIndex(
            (item) => item.id === speciesItem.id,
          );
        } else if (speciesItem.arrayIndex !== undefined) {
          existingIndex = existingSpeciesItems.findIndex(
            (item) => item.arrayIndex === speciesItem.arrayIndex,
          );
        }

        const selectedSpeciesType = activitySpeciesTypes.find(
          (type) => type.id === speciesItem.activitySpeciesTypeId,
        );
        const selectedGender = gendersData?.find(
          (gender) => gender.id === speciesItem.genderId,
        );

        // Use speciesOther if "Not on list" is selected, otherwise use the species type title
        const speciesTypeTitle = speciesItem.activitySpeciesTypeId === ActivitySpeciesTypeConstants.NOT_ON_LIST
          ? speciesItem.speciesOther
          : selectedSpeciesType?.title;

        const enrichedSpeciesItem = {
          ...speciesItem,
          activitySpeciesTypeTitle: speciesTypeTitle,
          genderTitle: selectedGender?.title,
          activitySpeciesType: undefined,
          gender: undefined,
        };

        if (existingIndex !== -1) {
          // Editing existing item
          console.log('Editing: ', enrichedSpeciesItem);
          return {
            ...prev,
            activitySpecies: existingSpeciesItems.map((item, index) => {
              if (index === existingIndex) {
                return enrichedSpeciesItem;
              }
              return item;
            }),
          };
        } else {
          // Adding new item - assign arrayIndex
          console.log('Adding: ', enrichedSpeciesItem);
          const newArrayIndex =
            Math.max(
              ...existingSpeciesItems.map((opt) => opt.arrayIndex || 0),
              0,
            ) + 1;
          const newSpeciesItem = {
            ...enrichedSpeciesItem,
            arrayIndex: newArrayIndex,
          };
          const newOptions = existingSpeciesItems.map((option) => ({
            ...option,
            isCorrectOption: false,
          }));

          return {
            ...prev,
            activitySpecies: [...newOptions, newSpeciesItem],
          };
        }
      });

      handleDrawerClose();
    }
  };

  const handleSpeciesItemEdit = (optionToEdit: ActivitySpeciesItem) => {
    setSpeciesItem(optionToEdit);
    setIsSpeciesItemDrawerOpen(true);
  };

  const handleSpeciesItemDelete = async (
    speciesToDelete: ActivitySpeciesDTO,
  ) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    setFormData((prev) => ({
      ...prev,
      activitySpecies:
        prev.activitySpecies?.filter(
          (option) => option.id !== speciesToDelete.id,
        ) || [],
    }));
  };

  const handleSpeciesTypeOther = () => {
    setSpeciesItem((prev) => ({
      ...prev,
      activitySpeciesTypeId: ActivitySpeciesTypeConstants.NOT_ON_LIST,
    }));
  };

  const sections = Object.values(FORM_SECTIONS);
  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      {roles?.includes('Admin') && FORM_SECTIONS.memberDetails && (
        <FormSection
          id={FORM_SECTIONS.memberDetails.id}
          title={FORM_SECTIONS.memberDetails.title}
        >
          <MemberDetails member={formData.member ?? {}} />
        </FormSection>
      )}

      <FormSection
        id={FORM_SECTIONS.activityDetails.id}
        title={FORM_SECTIONS.activityDetails.title}
      >
        <FormField
          id="activityTypeId"
          label="Activity Type"
          type="dropdown"
          value={formData.activityTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Activity Type"
          required={true}
          error={errors.activityTypeId}
          options={activityTypesData.map((type) => ({
            value: type.id,
            label: type.title || '',
          }))}
          isLoading={isLoadingActivityTypes}
        />
        {formData.activityTypeId === 'Not on list' && (
          <FormField
            id="typeOther"
            label="Type Other"
            type="text"
            value={formData.typeOther || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Type Other"
            required={false}
            error={errors.typeOther}
          />
        )}
        {formData.activityTypeId ===
          ActivityTypeConstants.NON_ACTIVE_REPORT && (
            <>
              <FormField
                id="nonActiveTypeId"
                label="Type of non-activity"
                type="dropdown"
                value={formData.nonActiveTypeId || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Select Non Active Type"
                required={true}
                error={errors.nonActiveTypeId}
                options={activityTypesData
                  .filter(
                    (type) => type.id !== ActivityTypeConstants.NON_ACTIVE_REPORT,
                  )
                  .map((type) => ({
                    value: type.id,
                    label: type.title || '',
                  }))}
                isLoading={isLoadingActivityTypes}
              />
              <FormField
                id="dateYear"
                label="Year of non-activity"
                type="dropdown"
                value={formData.dateYear || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Select non-active year"
                required={true}
                error={errors.dateYear}
                options={years.map((year) => ({
                  value: year,
                  label: year,
                }))}
              />
              <FormField
                id="activityReason"
                label="Reason for no activity"
                type="textarea"
                value={formData.activityReason || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Please supply a reason for no activity"
                required={true}
                error={errors.activityReason}
              />
            </>
          )}
        {formData.activityTypeId && (
          <>
            {(formData.activityTypeId === ActivityTypeConstants.HUNT_RELATED ||
              formData.activityTypeId ===
              ActivityTypeConstants.SPORT_SHOOTING) && (
                <>
                  <FormField
                    id="activityCategoryId"
                    label={`Type of ${formData.activityTypeId === ActivityTypeConstants.HUNT_RELATED ? 'hunt' : 'Sport-Shooting'} related activity`}
                    type="dropdown"
                    value={formData.activityCategoryId || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Select Activity Category"
                    required={true}
                    error={errors.activityCategoryId}
                    options={activityCategories.map((type) => ({
                      value: type.id,
                      label: type.title || '',
                    }))}
                    isLoading={isLoadingActivityCategories}
                  />
                  {formData.activityCategoryId === 'Not on List' && (
                    <>
                      <FormField
                        id="categoryOther"
                        label="Category Other"
                        type="text"
                        value={formData.categoryOther || ''}
                        onChange={(e) => handleChange(e, setFormData)}
                        placeholder="Enter Category Other"
                        required={false}
                        error={errors.categoryOther}
                      />
                    </>
                  )}

                  {formData.activityCategoryId && (
                    <>
                      {AnimalHunting.includes(formData.activityCategoryId) && (
                        <>
                          <FormField
                            id="leaveDate"
                            label="Date leave for activity"
                            type="date"
                            value={formData.leaveDate || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Select Leave Date"
                            allowFutureDates={false}
                            required={true}
                            error={errors.leaveDate}
                          />
                          <FormField
                            id="returnDate"
                            label="Date back from activity"
                            type="date"
                            value={formData.returnDate || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Select Return Date"
                            allowFutureDates={false}
                            required={true}
                            error={errors.returnDate}
                          />
                        </>
                      )}

                      {!AnimalHunting.includes(formData.activityCategoryId) && (
                          <FormField
                            id="activityDate"
                            label="Date of activity"
                            type="date"
                            value={formData.activityDate || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Enter Activity Date"
                            allowFutureDates={false}
                            required={true}
                            error={errors.activityDate}
                          />
                        )}

                      {IncludeNatureOfMeeting.includes(
                        formData.activityCategoryId,
                      ) && (
                          <FormField
                            id="activityReason"
                            label="Nature of meeting"
                            type="textarea"
                            value={formData.activityReason || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Please supply a reason for the nature of meeting"
                            required={true}
                            error={errors.activityReason}
                          />
                        )}

                      {IncludeTypeActivity.includes(
                        formData.activityCategoryId,
                      ) && (
                          <FormField
                            id="typeActivity"
                            label="Type or Name of course"
                            type="text"
                            value={formData.typeActivity || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Type or Name of course"
                            required={true}
                            error={errors.typeActivity}
                          />
                        )}

                      {IncludeDescriptionOfActivity.includes(
                        formData.activityCategoryId,
                      ) && (
                          <FormField
                            id="activityReason"
                            label="Description of Activity"
                            type="textarea"
                            value={formData.activityReason || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Please supply a description for the activity"
                            required={true}
                            error={errors.activityReason}
                          />
                        )}
                    </>
                  )}

                  <FormField
                    id="countryId"
                    label="Country"
                    type="dropdown"
                    value={formData.countryId || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Select Country"
                    required={true}
                    error={errors.countryId}
                    options={(countriesData || []).map((type) => ({
                      value: type.id,
                      label: type.title || '',
                    }))}
                    isLoading={isLoadingCountries}
                  />
                  <FormField
                    id="provinceId"
                    label="Province/Region"
                    type="dropdown"
                    value={formData.provinceId || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Select Province"
                    required={
                      formData.countryId === CountryConstant.SOUTH_AFRICA ||
                      !formData.countryId
                    }
                    error={errors.provinceId}
                    options={provinceOptions}
                    isLoading={isLoadingProvinces}
                  />
                  {shouldShowProvinceOther && (
                    <FormField
                      id="provinceOther"
                      label="Other province/region"
                      type="text"
                      value={formData.provinceOther || ''}
                      onChange={(e) => handleChange(e, setFormData)}
                      placeholder="Add other province or region"
                      required={shouldShowProvinceOther}
                      error={errors.provinceOther}
                    />
                  )}
                  <FormField
                    id="town"
                    label="Closest City/Town"
                    type="text"
                    value={formData.town || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Add closest city or town"
                    required={true}
                    error={errors.town}
                  />
                  <FormField
                    id="location"
                    label="Location of activity"
                    type="text"
                    value={formData.location || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Add range or farm name"
                    required={true}
                    error={errors.location}
                  />
                  {roles?.includes('Admin') &&
                    (formData.activityCategoryId ===
                      ActivityCategoryConstants.RELOADING_DEVELOPMENT_OF_HUNTING_AMMUNITION_LOAD ||
                      formData.activityCategoryId ===
                      ActivityCategoryConstants.RELOADING_DEVELOPMENT_OF_SPORT_SHOOTING_AMMUNITION_LOAD) && (
                      <>
                        <FormField
                          id="unitsLoaded"
                          label="Number of rounds loaded"
                          type="text"
                          value={formData.unitsLoaded || ''}
                          onChange={(e) => handleChange(e, setFormData)}
                          placeholder="Add number of rounds loaded"
                          required={false}
                          error={errors.unitsLoaded}
                        />
                        <FormField
                          id="propellant"
                          label="Propellant used"
                          type="text"
                          value={formData.propellant || ''}
                          onChange={(e) => handleChange(e, setFormData)}
                          placeholder="Add propellant used"
                          required={false}
                          error={errors.propellant}
                        />
                        <FormField
                          id="casing"
                          label="Casing used"
                          type="text"
                          value={formData.casing || ''}
                          onChange={(e) => handleChange(e, setFormData)}
                          placeholder="Add casing (brass) used"
                          required={false}
                          error={errors.casing}
                        />
                        <FormField
                          id="bulletWeight"
                          label="Weight & Make of Bullet"
                          type="text"
                          value={formData.bulletWeight || ''}
                          onChange={(e) => handleChange(e, setFormData)}
                          placeholder="Add weight and make of bullet"
                          required={false}
                          error={errors.bulletWeight}
                        />
                        <FormField
                          id="reloadingPress"
                          label="Reloading Press Used"
                          type="text"
                          value={formData.reloadingPress || ''}
                          onChange={(e) => handleChange(e, setFormData)}
                          placeholder="Add reloading press used"
                          required={false}
                          error={errors.reloadingPress}
                        />
                        <FormField
                          id="makeOfDie"
                          label="Make of die set"
                          type="text"
                          value={formData.makeOfDie || ''}
                          onChange={(e) => handleChange(e, setFormData)}
                          placeholder="Add make of die set"
                          required={false}
                          error={errors.makeOfDie}
                        />
                        <FormField
                          id="speed"
                          label="Average speed"
                          type="text"
                          value={formData.speed || ''}
                          onChange={(e) => handleChange(e, setFormData)}
                          placeholder="Add average speed"
                          required={false}
                          error={errors.speed}
                        />
                      </>
                    )}
                  {formData.activityCategoryId && (
                    <>
                      {!ExcludeMemberFirearm.includes(
                        formData.activityCategoryId,
                      ) && (
                          <FormField
                            id="memberFirearmId"
                            label="Firearm"
                            type="dropdown"
                            value={formData.memberFirearmId || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Select your Firearm"
                            required={true}
                            error={errors.memberFirearmId}
                            options={buildFirearmOptions(memberFirearms)}
                            isLoading={isLoadingMemberFirearms}
                            helper={
                              <>
                                Only complete and active firearms will display in the list.
                                <br />
                                Not on list, <Link to="/admin/memberfirearms" className="text-blue-600 dark:text-blue-400 hover:underline">manage</Link> or <Link to="/admin/memberfirearms/create" className="text-blue-600 dark:text-blue-400 hover:underline">add a new firearm</Link>?
                              </>
                            }
                          />
                        )}
                      {AnimalHunting.includes(formData.activityCategoryId) && (
                        <FormField
                          id="isHuntAnimal"
                          label="Did you hunt any animals?"
                          type="checkbox"
                          value={formData.isHuntAnimal || ''}
                          onChange={(e) => handleChange(e, setFormData)}
                          helper="Check to confirm"
                          required={false}
                          error={errors.isHuntAnimal}
                        />
                      )}
                    </>
                  )}
                </>
              )}
          </>
        )}

        {roles?.includes('User') && (
          <FormField
            id="notes"
            label="Notes (Optional)"
            type="textarea"
            value={formData.notes || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Add any notes to your activity"
            required={false}
            error={errors.notes}
          />
        )}
      </FormSection>

      {formData.isHuntAnimal && (
        <FormSection
          id={FORM_SECTIONS.specifySpecies.id}
          title={FORM_SECTIONS.specifySpecies.title}
          headerActions={
            <AddButton
              onClick={() => setIsSpeciesItemDrawerOpen(true)}
              variant="secondary"
              label="Add Species"
            />
          }
        >
          <ActivitySpeciesTable
            data={formData.activitySpecies || []}
            handleEdit={(dto) => handleSpeciesItemEdit(dto)}
            handleDelete={(dto) => handleSpeciesItemDelete(dto)}
            currentPage={currentPage}
            totalPages={Math.ceil(
              (formData.activitySpecies?.length || 0) / pageSize,
            )}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            isLoading={false}
          />
          <Drawer
            isOpen={isSpeciesItemDrawerOpen}
            onClose={handleDrawerClose}
            title={speciesItem ? 'Edit Species Item' : 'Add Species Item'}
          >
            <div className="space-y-4">
              <FormField
                id="activitySpeciesTypeId"
                label="Type"
                type="dropdown"
                value={speciesItem.activitySpeciesTypeId || ''}
                onChange={(e) => handleChange(e, setSpeciesItem)}
                placeholder="Select Species Type"
                required={true}
                error={speciesItemErrors.activitySpeciesTypeId}
                options={activitySpeciesTypes.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingActivitySpeciesTypes}
                helper={
                  <span>
                    Not on list?{' '}
                    <a
                      className="cursor-pointer text-primary"
                      onClick={handleSpeciesTypeOther}
                    >
                      Click here
                    </a>
                  </span>
                }
              />
              {speciesItem.activitySpeciesTypeId === ActivitySpeciesTypeConstants.NOT_ON_LIST && (
                <FormField
                  id="speciesOther"
                  label="Species Name"
                  type="text"
                  value={speciesItem.speciesOther || ''}
                  onChange={(e) => handleChange(e, setSpeciesItem)}
                  placeholder="Enter Species Name"
                  required={false}
                  error={speciesItemErrors.speciesOther}
                />
              )}

              <FormField
                id="genderId"
                label="Gender"
                type="dropdown"
                value={speciesItem.genderId || ''}
                onChange={(e) => handleChange(e, setSpeciesItem)}
                placeholder="Select Gender"
                required={true}
                error={speciesItemErrors.genderId}
                options={(gendersData || []).map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingGenders}
              />
              <FormField
                id="total"
                label="Number hunted"
                type="number"
                value={speciesItem.total || ''}
                onChange={(e) => handleChange(e, setSpeciesItem)}
                placeholder="Enter Total"
                required={true}
                error={speciesItemErrors.total}
              />
              <div className="flex justify-end space-x-4">
                <Button variant="secondary" onClick={handleDrawerClose}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSpeciesItemSave}>
                  {speciesItem.id ? 'Save Changes' : 'Add Option'}
                </Button>
              </div>
            </div>
          </Drawer>
        </FormSection>
      )}

      <FormSection
        id={FORM_SECTIONS.uploadProof.id}
        title={FORM_SECTIONS.uploadProof.title}
      >
        {formData.confirmationFileId && (
          <ImageLink
            fileId={formData.confirmationFileId}
            label="Proof of Activity"
          />
        )}
        <FileUpload
          key={`confirmationFile-${formResetKey}`}
          id="confirmationFileId"
          onChange={handleFileChange('confirmationFileId')}
          title="Proof of Activity"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={true}
          error={errors.confirmationFileId}
          isUploading={uploadingFiles['confirmationFileId']}
        />

        {formData.additionalConfirmationFile1Id && (
          <ImageLink
            fileId={formData.additionalConfirmationFile1Id}
            label="Confirmation file (Additional)"
          />
        )}
        <FileUpload
          key={`additionalConfirmationFile1-${formResetKey}`}
          id="additionalConfirmationFile1Id"
          onChange={handleFileChange('additionalConfirmationFile1Id')}
          title="Confirmation file (Additional)"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={false}
          error={errors.additionalConfirmationFile1Id}
          isUploading={uploadingFiles['additionalConfirmationFile1Id']}
        />

        {formData.additionalConfirmationFile2Id && (
          <ImageLink
            fileId={formData.additionalConfirmationFile2Id}
            label="Confirmation file (Additional 2)"
          />
        )}
        <FileUpload
          key={`additionalConfirmationFile2-${formResetKey}`}
          id="additionalConfirmationFile2Id"
          onChange={handleFileChange('additionalConfirmationFile2Id')}
          title="Confirmation file (Additional 2)"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={false}
          error={errors.additionalConfirmationFile2Id}
          isUploading={uploadingFiles['additionalConfirmationFile2Id']}
        />
      </FormSection>
      {roles?.includes('Admin') && FORM_SECTIONS.feedback && (
        <FormSection
          id={FORM_SECTIONS.feedback.id}
          title={FORM_SECTIONS.feedback.title}
        >
          <FeedbackForm
            componentId={ComponentConstants.ACTIVITIES}
            recordId={formData.id ?? ''}
            initialData={formData}
            componentStatuses={activityStatusesData.map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            statusFieldName={statusFieldName}
            onChange={(updatedData) => {
              setFormData((prev) => ({
                ...prev,
                ...updatedData,
              }));
            }}
          />
          <div>
            <label>Approval email sent: </label>
            <br></br>
            <span>
              {renderBooleanBadge(
                formData.emailFeedbackSentDate ? true : false,
                'Yes',
                'No',
              )}
            </span>
          </div>
        </FormSection>
      )}
      {roles?.includes('Admin') && FORM_SECTIONS.notes && (
        <FormSection
          id={FORM_SECTIONS.notes.id}
          title={FORM_SECTIONS.notes.title}
          columns={1}
        >
          <FormField
            id="notes"
            label="Notes (Optional)"
            type="textarea"
            value={formData.notes || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Add any notes to your activity"
            required={false}
            error={errors.notes}
          />
        </FormSection>
      )}
      {roles?.includes('Admin') && FORM_SECTIONS.history && (
        <FormSection
          id={FORM_SECTIONS.history.id}
          title={FORM_SECTIONS.history.title}
        >
          <AuditLogTable
            data={auditLogs}
            currentPage={auditCurrentPage}
            totalPages={auditTotalPages}
            setCurrentPage={setAuditCurrentPage}
            pageSize={auditPageSize}
            setPageSize={setAuditPageSize}
            isLoading={isLoadingAuditLogs}
          />
        </FormSection>
      )}
    </Form>
  );
};

export default ActivityForm;
